import React, { useEffect, useMemo, useState } from "react";
import { Download, Plus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import AttentionChip from "./AttentionChip";
import AttentionPaymentsTable from "./AttentionPaymentsTable";
import NotYetRecordedList from "./NotYetRecordedList";
import ContractsMissingPaymentList from "./ContractsMissingPaymentList";
import MemberDirectoryWarning from "./MemberDirectoryWarning";
import FormModal from "./FormModal";
import PaymentForm from "./PaymentForm";
import ContractForm from "./ContractForm";
import CollectionAccountCard from "./CollectionAccountCard";
import {
  selectBillingSummary,
  selectBillingSummaryStatus,
} from "../../features/billing/billing-slice";
import { fetchBillingSummary } from "../../features/billing/slice-extensions/billing-slice-extension";
import { fetchContracts } from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { selectContracts } from "../../features/contracts/contracts-slice";
import { ContractStatus } from "../../features/contracts/models/ContractStatus";
import { ContractType } from "../../features/contracts/models/ContractType";
import { Payment } from "../../features/payments/models/Payment";
import {
  AttentionFilter,
  matchesAttentionFilter,
} from "../../features/billing/utils/billingFilterUtils";
import { formatMoney } from "../../features/billing/utils/money";
import { exportToExcel } from "../../features/billing/utils/excelExport";
import { paymentShapeLabel } from "../../features/payments/utils/paymentLabels";
import { useMemberNameLookup } from "../../features/members/hooks/useMemberNameLookup";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

/** The quick "new pack" flow: a pack payment against a known contract, or a new pack contract first. */
type NewPackModal =
  | { kind: "payment"; contractId: string }
  | { kind: "contract"; memberId?: string };

/**
 * "Needs attention" view: chip row doubling as filters, the filtered Payments
 * with quick actions, and members whose recent visits exceed their remaining
 * pack capacity.
 */
const BillingOverviewTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const summary = useAppSelector(selectBillingSummary);
  const status = useAppSelector(selectBillingSummaryStatus);
  const contracts = useAppSelector(selectContracts);
  const memberName = useMemberNameLookup();

  const [newPackModal, setNewPackModal] = useState<NewPackModal | null>(null);

  const filter = (searchParams.get("attention") as AttentionFilter | null) ?? "all";

  useEffect(() => {
    dispatch(fetchBillingSummary());
    // Quick actions need the contract list (to find a member's pack) and the
    // price catalog (for the create forms).
    dispatch(fetchContracts({ page: 1, pageSize: 100 }));
    dispatch(fetchPaymentTypes({ includeArchived: false }));
  }, [dispatch]);

  const filteredPayments = useMemo(() => {
    if (!summary) return [];
    return summary.attentionPayments.filter((p) => matchesAttentionFilter(p, filter));
  }, [summary, filter]);

  const setFilter = (next: AttentionFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("attention");
    else params.set("attention", next);
    setSearchParams(params);
  };

  const refresh = () => dispatch(fetchBillingSummary());

  /** Pack quick action from a payment row: reuse its contract directly. */
  const openNewPackForPayment = (payment: Payment) => {
    if (payment.contractId) {
      setNewPackModal({ kind: "payment", contractId: payment.contractId });
    } else {
      setNewPackModal({ kind: "contract", memberId: payment.memberId });
    }
  };

  /** Pack quick action from the not-yet-recorded list: find the member's active pack. */
  const openNewPackForMember = (memberId: string) => {
    const pack = contracts.find(
      (c) =>
        c.memberId === memberId &&
        c.type === ContractType.VisitPack &&
        c.status === ContractStatus.Active,
    );
    setNewPackModal(pack
      ? { kind: "payment", contractId: pack.id }
      : { kind: "contract", memberId });
  };

  const closeModal = () => setNewPackModal(null);
  const onSaved = () => {
    closeModal();
    refresh();
    dispatch(fetchContracts({ page: 1, pageSize: 100 }));
  };

  const handleExport = () => {
    exportToExcel(
      "billing-attention",
      filteredPayments.map((p) => ({
        [t("billing.table.status")]: t(`billing.paymentStatus.${p.status}`, { defaultValue: p.status }),
        [t("billing.table.shape")]: paymentShapeLabel(p.shape, t),
        [t("billing.table.member")]: memberName(p.memberId),
        [t("billing.table.amount")]: formatMoney(p.amountMinorUnits, p.amountCurrency, getDisplayLocale()),
        [t("billing.table.remainingBalance")]: p.remainingBalance ?? "",
        [t("billing.table.duePeriodStart")]: p.dueDate ?? p.billingPeriodStart ?? "",
      })),
    );
  };

  const counts = summary?.counts;

  return (
    <div>
      <MemberDirectoryWarning />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {counts && (
          <>
            <AttentionChip label={t("billing.attention.all")} count={summary!.attentionPayments.length} variant="neutral" onClick={() => setFilter("all")} />
            <AttentionChip label={t("billing.attention.drafts")} count={counts.drafts} variant="info" onClick={() => setFilter("drafts")} />
            <AttentionChip label={t("billing.attention.overdue")} count={counts.overdue} variant="alert" onClick={() => setFilter("overdue")} />
            <AttentionChip label={t("billing.attention.notYetRecorded")} count={counts.notYetRecorded} variant="warning" onClick={() => setFilter("notYetRecorded")} />
            <AttentionChip label={t("billing.attention.lowBalance")} count={counts.lowBalance} variant="warning" onClick={() => setFilter("lowBalance")} />
            <AttentionChip label={t("billing.attention.invoiced")} count={counts.invoiced} variant="neutral" onClick={() => setFilter("invoiced")} />
          </>
        )}
        {summary && filteredPayments.length > 0 && (
          <button type="button" className="btn btn-secondary ml-auto" onClick={handleExport}>
            <Download size={16} />
            <span>{t("billing.common.exportToExcel")}</span>
          </button>
        )}
      </div>

      {status === LoadingStatus.Loading && !summary && (
        <div className="mt-6"><TableRowsSkeleton rows={5} /></div>
      )}

      {summary && contracts.length === 0 && (
        <div className="mt-6 rounded-2xl border border-grey-90 p-6 dark:border-primary-20">
          <h3 className="text-grey-10 dark:text-grey-90">{t("billing.overview.noContractsTitle")}</h3>
          <p className="mt-2 max-w-xl text-sm text-grey-40 dark:text-grey-60">
            {t("billing.overview.noContractsText")}
          </p>
          <button
            type="button"
            className="btn btn-primary mt-4"
            onClick={() => setNewPackModal({ kind: "contract" })}
          >
            <Plus size={18} strokeWidth={2} />
            <span>{t("billing.actions.newContract")}</span>
          </button>
        </div>
      )}

      {summary && contracts.length > 0 && filteredPayments.length === 0 && (
        <p className="mt-6 text-sm text-grey-40 dark:text-grey-80">{t("billing.overview.emptyFilter")}</p>
      )}

      {summary && filteredPayments.length > 0 && (
        <AttentionPaymentsTable
          payments={filteredPayments}
          memberName={memberName}
          onChanged={refresh}
          onNewPack={openNewPackForPayment}
        />
      )}

      {summary && (
        <NotYetRecordedList
          members={summary.notYetRecordedMembers}
          memberName={memberName}
          onNewPack={openNewPackForMember}
        />
      )}

      {summary && (
        <ContractsMissingPaymentList
          contracts={summary.contractsMissingPayment}
          memberName={memberName}
          onChanged={refresh}
        />
      )}

      <div className="mt-6">
        <CollectionAccountCard />
      </div>

      {newPackModal?.kind === "payment" && (
        <FormModal onClose={closeModal}>
          <PaymentForm mode="pack" initialContractId={newPackModal.contractId}
            onSaved={onSaved} onClose={closeModal} />
        </FormModal>
      )}
      {newPackModal?.kind === "contract" && (
        <FormModal onClose={closeModal}>
          <ContractForm initialMemberId={newPackModal.memberId}
            onSaved={onSaved} onClose={closeModal} />
        </FormModal>
      )}
    </div>
  );
};

export default BillingOverviewTab;
